/**
 * The sentence the Traffic card shows under a live trace when a tick brought
 * no reading — pure, so the one branch that was got wrong is pinned in node.
 *
 * The sample route answers a refused tick with the series UNCHANGED and a
 * `refusal` sentence, and the card once captioned every one of them with
 * "The meter picks up when the chassis is free." But only ONE refusal is the
 * chassis being busy — the lock skip the route mints itself
 * (`apps/api/src/routes/onu-traffic.ts`, `withOltLockIfIdle` returning null).
 * The rest come from the reader and mean something else entirely: "it is
 * probably offline" (the ZTE interface reports no rate), "Could not read
 * throughput: …" (the telnet session failed), "No CLI credentials…", and on
 * Huawei "SNMP read failed: …" / "Could not resolve this ONU’s SNMP index."
 * An ONU that dropped mid-meter — the exact thing a technician watches live
 * for — read "…it is probably offline. The meter picks up when the chassis is
 * free.": a false cause and a wait for something that would never come. So
 * the promise is added to the busy sentence alone; everything else is shown
 * as the server wrote it, the way the manual button's flash already does.
 *
 * The busy sentence is mirrored here rather than carried as a flag because
 * the route is the only place it is minted and the wire shape is a plain
 * string; `traffic-refusal.test.ts` reads the route source and fails the
 * moment the two drift, so a rewording there cannot quietly turn this back
 * into a caption for every refusal.
 */

/** The route's own words for a tick skipped because the OLT was locked by an operator's work. */
export const BUSY_CHASSIS_REFUSAL = 'The chassis was busy; no sample was taken this time.';

/** What the card adds after a busy refusal — and after nothing else. */
export const BUSY_CHASSIS_FOLLOW_UP = 'The meter picks up when the chassis is free.';

/** Whether a refusal is the lock skip, as opposed to a reader that could not get a figure. */
export function isBusyChassisRefusal(refusal: string | null | undefined): boolean {
  return typeof refusal === 'string' && refusal.trim() === BUSY_CHASSIS_REFUSAL;
}

/**
 * The line to show under a live trace for the latest tick's refusal: null when
 * the tick read fine, the busy sentence plus its follow-up, or any other
 * refusal verbatim — the server's sentence already says what happened.
 */
export function liveRefusalNotice(refusal: string | null | undefined): string | null {
  if (typeof refusal !== 'string') return null;
  const text = refusal.trim();
  if (!text) return null;
  return isBusyChassisRefusal(text) ? `${text} ${BUSY_CHASSIS_FOLLOW_UP}` : text;
}
